import { useEffect, useRef, type RefObject } from 'react';

/** Selector matching the elements considered keyboard-focusable inside a trap. */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** Collect the visible, focusable descendants of a container, in DOM order. */
function focusableWithin(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => element.offsetParent !== null || element === document.activeElement,
  );
}

/**
 * Trap keyboard focus inside a container while `active`, per the WCAG modal
 * pattern (spec 08 §7): accessible modals/drawers must trap focus, restore it on
 * close, and dismiss with `Esc`.
 *
 * Behaviour while active:
 * - Moves focus to the first focusable element (or the container itself).
 * - Cycles Tab / Shift+Tab within the container so focus never escapes.
 * - Calls `onEscape` when the user presses `Esc`.
 * - On deactivation/unmount, returns focus to whatever was focused before.
 *
 * The container must be focusable as a fallback — give it `tabIndex={-1}`.
 */
export function useFocusTrap(
  active: boolean,
  containerRef: RefObject<HTMLElement>,
  onEscape: () => void,
): void {
  // Keep the escape handler in a ref so the trap effect does NOT depend on its
  // identity. Callers routinely pass a fresh inline `onClose` on every render
  // (common when the form state lives in the parent), and if the effect re-ran on
  // each render it would re-focus the first element (the close button) after every
  // keystroke. With the ref, the effect runs once per open — focus lands once.
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;

  useEffect(() => {
    if (!active) {
      return;
    }
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const initial = focusableWithin(container)[0] ?? container;
    initial.focus();

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onEscapeRef.current();
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }
      const focusables = focusableWithin(container);
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) {
        event.preventDefault();
        container.focus();
        return;
      }
      const activeElement = document.activeElement;
      if (event.shiftKey && activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
    // Intentionally NOT depending on `onEscape` — it is read through a ref so the
    // trap installs once per open (see the ref comment above).
  }, [active, containerRef]);
}
